import { datadogRum } from '@datadog/browser-rum';
import { FormViewer } from '@momos/react-elements';
import { Spin } from 'antd';
import MetaHeader from 'components/MetaHeader';
import endpoints from 'config/endpoints';
import request from 'config/request';
import LanguageSelector from 'components/LanguageSelector';
import { DEFAULT_TS_TEXT, basePath } from 'constant';
import { FORM_TEMPLATES, WINBACK_DUMMY_REVIEW_DATA } from 'constant/constants';
import { INQUIRYFORM_QUESTION_TYPE, INQUIRYFORM_THEME } from 'constant/inquiryForm';
import { isEmpty } from 'lodash';
import find from 'lodash/find';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import sortBy from 'lodash/sortBy';
import { useLocaleAttributes } from 'hooks/useLocaleAttributes';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Script from 'next/script';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getInquiryFormById, getInquiryFormBySlug, getReviewBasicDetails } from 'services';
import styled, { createGlobalStyle } from 'styled-components';
import { InquiryFormData, InquiryFormFileType, InquiryFormQuestionType, ReviewDetails } from 'utils/types';

interface BrandsInquiryFormPageProps {
  inquiryFormData: InquiryFormData;
  reviewDetails: ReviewDetails;
  supportedLocales: string[];
  defaultLocale: string | null;
  initialLocale: string | null;
}

const GENERAL_TYPE_NAME = '$$__General__$$';

function BrandsInquiryFormPage({
  inquiryFormData,
  reviewDetails,
  supportedLocales,
  defaultLocale,
  initialLocale,
}: BrandsInquiryFormPageProps) {
  const [pageLoader, setPageLoader] = useState(true);
  const micrositeTheme = inquiryFormData?.config?.styling || INQUIRYFORM_THEME;
  const currentLocale = initialLocale;
  const activeLocale = currentLocale || defaultLocale || 'en-US';
  useLocaleAttributes(currentLocale || defaultLocale);

  const router = useRouter();

  const navigateToLocale = useCallback(
    (locale: string | null) => {
      const query = omit(router.query, 'locale');
      router.replace({ pathname: router.pathname, query: locale ? { ...query, locale } : query }, undefined, {
        scroll: false,
      });
    },
    [router],
  );

  useEffect(() => {
    setTimeout(() => {
      setPageLoader(false); // page loader for survey
    }, 700);
  }, []);

  const inquiryTypes = useMemo(() => {
    return inquiryFormData?.inquiryTypes?.filter((inquiryType) => !isEqual(inquiryType, GENERAL_TYPE_NAME));
  }, [inquiryFormData]);

  const responseBasedOnQuestionType = (
    questionType: string,
    responseText: string,
    fileList: InquiryFormFileType[],
    inquiryTypeSelected: string,
  ) => {
    if (questionType == INQUIRYFORM_QUESTION_TYPE.INQUIRY_TYPE) return inquiryTypeSelected;
    if (questionType == INQUIRYFORM_QUESTION_TYPE.FILE_UPLOAD && fileList?.length > 0)
      return JSON.stringify(fileList.map(({ id, name, url }) => ({ id, name, url })));
    return responseText ? responseText : '';
  };

  const handleSubmit = async (
    { questionsResponses, inquiryTypeSelected, values }: any,
    callback: (success: boolean) => void,
  ) => {
    const requestBody: Record<string, any> = {
      inquiryFormId: inquiryFormData?.id,
      responses: questionsResponses.map(
        ({ id, questionText, questionType, responseText, fileList }: InquiryFormQuestionType) => ({
          questionId: id,
          questionText,
          questionType,
          responseText: responseBasedOnQuestionType(questionType, responseText, fileList, inquiryTypeSelected),
        }),
      ),
      inquiryTypeSelected,
      reviewId: reviewDetails?.momosReviewId,
      optin: inquiryFormData?.config?.general?.showOptin ? !!values?.optin : undefined,
    };

    const outletId = find(questionsResponses, { questionType: INQUIRYFORM_QUESTION_TYPE.OUTLET_SELECT })?.responseText;
    const inquiryType = find(inquiryTypes, { name: inquiryTypeSelected });

    if (supportedLocales.length > 0) {
      requestBody.locale = activeLocale;
    }

    requestBody.outletId = outletId || null;
    requestBody.inquiryTypeSelectedId = inquiryType?.id;
    requestBody.inquiryTypeSelectedMasterId = inquiryType?.master_inquiry_type_id;

    // Defense-in-depth observability for the recurring "location validation missed" bug
    // (see Notion: Recurring: location validation missed during survey submission).
    // The core fix lives in @momos/react-elements FormViewer (collapsing the conditional-
    // question render race), but this guard reports any future occurrence to Datadog so it
    // never goes silent again. Detection: an OUTLET_SELECT question is configured as
    // required for the selected inquiry type, but the submission has no outletId.
    const requiresOutlet = inquiryFormData?.questionTypes?.some(
      (q: InquiryFormQuestionType) =>
        q.questionType === INQUIRYFORM_QUESTION_TYPE.OUTLET_SELECT &&
        q.isRequired &&
        (q.types as Array<{ name?: string }>)?.some((t) => t?.name === inquiryTypeSelected),
    );
    if (requiresOutlet && !outletId) {
      // Wrapped in try/catch so observability never breaks submission — datadogRum is only
      // initialized for allowlisted forms in _app.tsx, and any failure here (uninitialized
      // SDK, transport error, etc.) must not block the user from submitting.
      try {
        datadogRum.addError(new Error('inquiry-form submission missing required outlet'), {
          inquiryFormId: inquiryFormData?.id,
          brandSlug: inquiryFormData?.brandSlug,
          inquiryTypeSelected,
          // outletQuestionRendered tells us whether the OUTLET_SELECT field even made it into
          // the submitted payload. When false this is the FormViewer render race; when true
          // it's a different bug (e.g. user cleared the value).
          outletQuestionRendered: Boolean(
            find(questionsResponses, { questionType: INQUIRYFORM_QUESTION_TYPE.OUTLET_SELECT }),
          ),
        });
      } catch (telemetryError) {
        // Telemetry must never block submit; log to console so dev tools surface it.
        console.warn('Failed to report missing-outlet telemetry event', telemetryError);
      }
    }

    try {
      await request.post(`${endpoints.momos.inquiryForm.default}`, requestBody);

      // send event to a parent window (see Notion: Guide: Listening for Form Submission Events).
      // outletId / locationName are null when the form has no location question or the selected
      // inquiry type does not show one. Fields are additive; existing host listeners keep working.
      const selectedOutlet = find(inquiryFormData?.outlets, (outlet) => String(outlet.id) === String(outletId));
      const gtagPayload = {
        formId: inquiryFormData?.id,
        eventName: 'Submit',
        eventType: 'Click',
        inquiryType: inquiryTypeSelected,
        outletId: outletId || null,
        locationName: selectedOutlet?.name ?? null,
      };
      window?.parent.postMessage(gtagPayload, '*');

      callback(true);
    } catch (error) {
      callback(false);
    }
  };

  const uploadFileRequest = async (formData: any, callback: (result: any, success: boolean) => void) => {
    try {
      const result = await request.post(`${endpoints.momos.inquiryForm.default}/upload`, formData, {
        headers: { 'content-type': 'multipart/form-data' },
      });

      callback(result, true);
    } catch (error) {
      callback(null, false);
    }
  };

  if (pageLoader) {
    return (
      <PageLoaderWrap>
        <Spin />
      </PageLoaderWrap>
    );
  }

  return (
    <>
      <MetaHeader
        title={inquiryFormData.brandName}
        image={inquiryFormData.config?.styling?.logoUrl}
        // pixelId={data?.micrositePixelId}
        fontFamilies={[
          get(inquiryFormData, 'config.styling.titleFontType'),
          get(inquiryFormData, 'config.styling.fontType'),
        ]}
      />
      {supportedLocales.length > 0 && (
        <LanguageSelector
          currentLocale={currentLocale}
          defaultLocale={defaultLocale}
          supportedLocales={supportedLocales}
          onChange={navigateToLocale}
          fontFamily={get(inquiryFormData, 'config.styling.fontType')}
          brandColor={get(inquiryFormData, 'config.styling.brandColor')}
        />
      )}
      <FormViewer
        locale={activeLocale}
        theme={micrositeTheme}
        brandName={inquiryFormData?.brandName}
        region={inquiryFormData?.region}
        thirdPartyLinks={inquiryFormData?.config?.thirdPartyLinks || []}
        logo={inquiryFormData?.config?.styling?.logo}
        heroBanner={inquiryFormData?.config?.styling?.heroBanner}
        showBrandName={inquiryFormData?.config?.general?.showBrandName}
        generalInfo={{
          ...inquiryFormData?.config?.general,
          showTermsConditions: get(inquiryFormData, 'config.general.showTermsConditions', true),
          termsConditions: get(inquiryFormData, 'config.general.termsConditions', DEFAULT_TS_TEXT),
          requiredTermsConditions: get(inquiryFormData, 'config.general.requiredTermsConditions', true),
        }}
        questionTypes={inquiryFormData?.questionTypes.filter((question) => question.status)}
        inquiryTypes={inquiryFormData?.inquiryTypes?.filter((inquiryType) => !isEqual(inquiryType, GENERAL_TYPE_NAME))}
        inquiryFormId={inquiryFormData?.id}
        outlets={sortBy(inquiryFormData?.outlets, 'name')}
        reviewDetails={
          isEmpty(reviewDetails) && isEqual(inquiryFormData.template, FORM_TEMPLATES.APPEASEMENT)
            ? WINBACK_DUMMY_REVIEW_DATA
            : reviewDetails
        }
        slug={inquiryFormData.brandSlug}
        onSubmit={handleSubmit}
        basePath={basePath}
        uploadFileRequest={uploadFileRequest}
      />
      <ThankyouModalStyle />

      <Script src="https://unpkg.com/iframe-resizer/js/iframeResizer.contentWindow.min.js" strategy="lazyOnload" />
    </>
  );
}

const PageLoaderWrap = styled.div`
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ThankyouModalStyle = createGlobalStyle`
  .thankyou-page-content {
    .body-content {
      p {
        font-family: inherit;
      }
    }
  }
`;

export const getServerSideProps: GetServerSideProps = async ({ params, query, req }) => {
  try {
    const { id, reviewId } = query;
    const localeParam: string | undefined = (query?.locale as string) || undefined;

    const [{ data: inquiryFormData }, reviewDetails] = await Promise.all([
      id ? getInquiryFormById(req, id, localeParam) : getInquiryFormBySlug(req, params?.slug, localeParam),
      reviewId ? getReviewBasicDetails(req, reviewId) : {},
    ]);

    const supportedLocales: string[] = inquiryFormData?.config?.supportedLocales ?? [];
    const defaultLocale: string | null = inquiryFormData?.config?.defaultLocale ?? null;
    const initialLocale = localeParam && supportedLocales.includes(localeParam) ? localeParam : null;

    const i18nProps = await serverSideTranslations(
      initialLocale || defaultLocale || 'en-US',
      ['common'],
      null,
      supportedLocales,
    );

    return {
      props: {
        inquiryFormData,
        reviewDetails: reviewDetails || {},
        supportedLocales,
        defaultLocale,
        initialLocale,
        ...i18nProps,
      },
    };
  } catch (err) {
    return {
      notFound: true,
    };
  }
};

export default BrandsInquiryFormPage;
